// src/composables/useMinors.ts
import { ref } from 'vue';

type MinorRow = { name: string; code: string };

export function useMinors() {
  const isLoading = ref(false);
  const errorMsg = ref('');

  function parseCSV(text: string): { rows: string[][]; delim: string } {
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const firstLine = (normalized.split('\n').find(l => l.trim().length) ?? '');
    const delim = [',', ';', '\t'].reduce((best, d) => {
      const score = (firstLine.match(new RegExp(`\\${d}`, 'g')) || []).length;
      return score > best.score ? { d, score } : best;
    }, { d: ',', score: -1 as number }).d;

    const rows: string[][] = [];
    let cur: string[] = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < normalized.length; i++) {
      const ch = normalized[i];
      const next = normalized[i + 1];

      if (inQuotes) {
        if (ch === '"' && next === '"') { field += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { field += ch; }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === delim) { cur.push(field); field = ''; }
        else if (ch === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
        else { field += ch; }
      }
    }
    if (field.length || cur.length) { cur.push(field); rows.push(cur); }
    return { rows: rows.filter(r => r.some(c => (c ?? '').trim().length)), delim };
  }

  function looksLikeCode(s: string) {
    const t = (s || '').trim();
    return /^[A-Z0-9]{4,}$/.test(t) && /MINU$/.test(t);
  }

  async function getActiveMinorsFromCsv(): Promise<MinorRow[]> {
    try {
      isLoading.value = true;
      errorMsg.value = '';

      const res = await fetch('/data/ActiveMinors2025.csv', { cache: 'no-store' });
      if (!res.ok) throw new Error(`CSV fetch failed: ${res.status} ${res.statusText}`);

      const text = await res.text();
      const { rows } = parseCSV(text);
      if (!rows.length) return [];

      let start = 0;
      let codeIdx = -1;
      let nameIdx = -1;

      const hdr = rows[0].map(v => v.trim().toLowerCase());
      const hdrHasNames = hdr.some(h => /(name|minor|title)/.test(h));
      const hdrHasCodes = hdr.some(h => /(code|abbr)/.test(h));

      if (hdrHasNames || hdrHasCodes) {
        codeIdx = hdr.findIndex(h => /(code|abbr)/.test(h));
        nameIdx = hdr.findIndex(h => /(name|minor|title)/.test(h));
        if (nameIdx < 0) nameIdx = 1;
        if (codeIdx < 0) codeIdx = 0;
        start = 1; 
      } else {
        const r0 = rows[0];
        if (r0.length >= 2) {
          if (looksLikeCode(r0[0])) { codeIdx = 0; nameIdx = 1; }
          else if (looksLikeCode(r0[1])) { codeIdx = 1; nameIdx = 0; }
          else { codeIdx = 0; nameIdx = 1; }
        } else {
          codeIdx = 0; nameIdx = 0;
        }
        start = 0; 
      }

      const minors: MinorRow[] = [];
      for (let i = start; i < rows.length; i++) {
        const r = rows[i];
        const name = (r[nameIdx] ?? '').trim();
        const code = (r[codeIdx] ?? '').trim();
        if (!name) continue;
        minors.push({ name, code });
      }

      const seen = new Set<string>();
      const unique = minors.filter(m => {
        const k = `${m.code}|${m.name}`.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      console.debug('[Minors CSV] Parsed rows:', unique.length);
      return unique;
    } catch (e: any) {
      console.error('[Minors CSV] Error:', e);
      errorMsg.value = e?.message ?? String(e);
      return [];
    } finally {
      isLoading.value = false;
    }
  }

  return { getActiveMinorsFromCsv, isLoading, errorMsg };
}
